import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneInput } from "@/components/ui/phone-input";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { SectionHead } from "./fields";
import { tempKey, type EmergencyContactDraft, type EmergencyContactErrors } from "./personFormSchema";
import { useOrgPhoneCountry } from "./useOrgPhoneCountry";

interface PersonEmergencyContactsSectionProps {
  value: EmergencyContactDraft[];
  onChange: (contacts: EmergencyContactDraft[]) => void;
  errors: EmergencyContactErrors;
}

/** Enter in a contact input must not submit the whole person form. */
const blockEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === "Enter") e.preventDefault();
};

/**
 * The Emergency tab of the person form: an editable list of emergency
 * contacts. Errors are shown inline beside each label; a row left entirely
 * blank is dropped on save.
 */
export function PersonEmergencyContactsSection({ value, onChange, errors }: PersonEmergencyContactsSectionProps) {
  const { t } = useTranslation("people");
  const defaultCountry = useOrgPhoneCountry();

  const update = (key: string, patch: Partial<EmergencyContactDraft>) =>
    onChange(value.map((contact) => (contact.key === key ? { ...contact, ...patch } : contact)));

  const add = () =>
    onChange([
      ...value,
      { key: tempKey("contact"), contact_name: "", relationship: "", phone: "", email: "", is_primary: value.length === 0 },
    ]);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <SectionHead className="mb-0">{t("form.sections.emergency")}</SectionHead>
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <Plus className="h-4 w-4 mr-2" />
          {t("form.emergency.add")}
        </Button>
      </div>

      {value.length === 0 && <p className="text-sm text-muted-foreground">{t("form.emergency.empty")}</p>}

      {value.map((contact, index) => {
        const rowErrors = errors[contact.key] ?? {};
        const id = (field: string) => `emergency-${contact.key}-${field}`;
        return (
          // One compact line per contact (name · relationship · phone · email ·
          // primary · remove) — no card per row inside the document card.
          <div
            key={contact.key}
            role="group"
            aria-label={t("form.emergency.contactN", { n: index + 1 })}
            className="grid grid-cols-1 gap-4 md:grid-cols-[repeat(4,minmax(0,1fr))_auto_auto] md:items-end"
          >
            <div className="space-y-2">
              {/* Every label sits in the same flex row (with or without an
                  error), so the four labels share one line box and align. */}
              <div className="flex items-center gap-2">
                <Label htmlFor={id("name")}>{t("form.emergency.name")}</Label>
                {rowErrors.contact_name && (
                  <span className="text-xs text-destructive">{rowErrors.contact_name}</span>
                )}
              </div>
              <Input
                id={id("name")}
                value={contact.contact_name}
                onChange={(e) => update(contact.key, { contact_name: e.target.value })}
                onKeyDown={blockEnter}
                className={cn(rowErrors.contact_name && "border-destructive")}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={id("relationship")}>{t("form.emergency.relationship")}</Label>
              </div>
              <Input
                id={id("relationship")}
                value={contact.relationship}
                onChange={(e) => update(contact.key, { relationship: e.target.value })}
                onKeyDown={blockEnter}
                placeholder={t("form.emergency.relationshipPlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label id={id("phone-label")}>{t("form.emergency.phone")}</Label>
              </div>
              {/* PhoneInput is two controls (country + number) and takes no id — name the group. */}
              <div role="group" aria-labelledby={id("phone-label")}>
                <PhoneInput
                  value={contact.phone}
                  onChange={(phone) => update(contact.key, { phone: phone ?? "" })}
                  placeholder={t("form.placeholders.phone")}
                  defaultCountry={defaultCountry}
                />
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor={id("email")}>{t("form.emergency.email")}</Label>
                {rowErrors.email && <span className="text-xs text-destructive">{rowErrors.email}</span>}
              </div>
              <Input
                id={id("email")}
                type="email"
                value={contact.email}
                onChange={(e) => update(contact.key, { email: e.target.value })}
                onKeyDown={blockEnter}
                className={cn(rowErrors.email && "border-destructive")}
              />
            </div>
            <div className="flex h-10 items-center space-x-2">
              <Checkbox
                id={id("primary")}
                checked={contact.is_primary}
                onCheckedChange={(checked) => update(contact.key, { is_primary: checked === true })}
                aria-label={t("form.emergency.primary")}
              />
              <Label htmlFor={id("primary")} className="font-normal">
                {t("form.emergency.primaryShort")}
              </Label>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              aria-label={t("form.emergency.remove", { n: index + 1 })}
              onClick={() => onChange(value.filter((c) => c.key !== contact.key))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
    </section>
  );
}
